/**
 * Session epoch + operation generation for Lot 4B push client ops.
 *
 * Same-tab invalidation is synchronous via listeners.
 * Cross-tab invalidation uses distinct event IDs published to storage — never
 * compares independently incremented per-tab counters as a shared total order.
 */

const SESSION_INVALIDATION_STORAGE_KEY = 'admin.prospectPush.invalidationEvent';

function createEventId() {
  try {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  return `inv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * @param {{
 *   storage?: Storage | { getItem: Function, setItem: Function, removeItem?: Function },
 *   subscribeStorage?: (handler: (event: { key: string|null, newValue: string|null }) => void) => () => void,
 *   createId?: () => string,
 * }} [options]
 */
export function createProspectPushSessionController({
  storage = typeof globalThis !== 'undefined' ? globalThis.localStorage : null,
  subscribeStorage = null,
  createId = createEventId,
} = {}) {
  let pushSessionEpoch = 1;
  let pushOperationGeneration = 1;
  let lastHandledInvalidationId = null;
  const listeners = new Set();
  let unsubscribeStorage = null;

  function notifyListeners(extra = {}) {
    const snapshot = {
      epoch: pushSessionEpoch,
      generation: pushOperationGeneration,
      ...extra,
    };
    for (const fn of listeners) {
      try {
        fn(snapshot);
      } catch {
        /* ignore listener errors */
      }
    }
  }

  function applyLocalBump({ reason = 'local', eventId = null } = {}) {
    pushSessionEpoch += 1;
    pushOperationGeneration += 1;
    notifyListeners({ reason, eventId });
    return pushSessionEpoch;
  }

  function publishInvalidationEvent(kind) {
    const event = {
      id: createId(),
      kind,
      t: Date.now(),
    };
    lastHandledInvalidationId = event.id;
    try {
      storage?.setItem?.(SESSION_INVALIDATION_STORAGE_KEY, JSON.stringify(event));
    } catch {
      /* ignore */
    }
    return event;
  }

  function handleRemoteInvalidation(raw) {
    if (typeof raw !== 'string' || !raw) return false;
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return false;
    }
    if (!parsed || typeof parsed.id !== 'string' || !parsed.id) return false;
    if (parsed.id === lastHandledInvalidationId) return false;
    lastHandledInvalidationId = parsed.id;
    // Always bump locally — do not compare remote counter magnitudes.
    applyLocalBump({ reason: 'remote', eventId: parsed.id });
    return true;
  }

  function ensureStorageBridge() {
    if (unsubscribeStorage) return;
    const defaultSubscribe = (handler) => {
      if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
        return () => {};
      }
      const onStorage = (event) => {
        handler({ key: event.key, newValue: event.newValue });
      };
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    };
    const subscribe = typeof subscribeStorage === 'function' ? subscribeStorage : defaultSubscribe;
    unsubscribeStorage = subscribe((event) => {
      if (event.key !== SESSION_INVALIDATION_STORAGE_KEY) return;
      handleRemoteInvalidation(event.newValue);
    });
  }

  ensureStorageBridge();

  return {
    getProspectPushSessionEpoch: () => pushSessionEpoch,
    getProspectPushOperationGeneration: () => pushOperationGeneration,
    subscribeProspectPushSessionInvalidation(listener) {
      if (typeof listener !== 'function') return () => {};
      ensureStorageBridge();
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    bumpProspectPushSessionEpoch() {
      const event = publishInvalidationEvent('session');
      return applyLocalBump({ reason: 'local-session', eventId: event.id });
    },
    bumpProspectPushOperationGeneration() {
      // Local-only — do not publish cross-tab (avoids reconcile noise).
      // Callers that must invalidate other tabs use bumpProspectPushSessionEpoch.
      pushOperationGeneration += 1;
      notifyListeners({ reason: 'local-operation', eventId: null });
      return pushOperationGeneration;
    },
    /** Test/helper: deliver a storage payload as another tab would. */
    __deliverRemoteInvalidation(raw) {
      return handleRemoteInvalidation(raw);
    },
    __resetForTests(epoch = 1, generation = 1) {
      pushSessionEpoch = epoch;
      pushOperationGeneration = generation;
      lastHandledInvalidationId = null;
      listeners.clear();
    },
    __getLastHandledInvalidationId: () => lastHandledInvalidationId,
    SESSION_INVALIDATION_STORAGE_KEY,
  };
}

const defaultSession = createProspectPushSessionController();

export { SESSION_INVALIDATION_STORAGE_KEY };

export function getProspectPushSessionEpoch() {
  return defaultSession.getProspectPushSessionEpoch();
}

export function getProspectPushOperationGeneration() {
  return defaultSession.getProspectPushOperationGeneration();
}

export function subscribeProspectPushSessionInvalidation(listener) {
  return defaultSession.subscribeProspectPushSessionInvalidation(listener);
}

export function bumpProspectPushSessionEpoch() {
  return defaultSession.bumpProspectPushSessionEpoch();
}

export function bumpProspectPushOperationGeneration() {
  return defaultSession.bumpProspectPushOperationGeneration();
}

export function createPushCleanupDelay(ms) {
  let id = null;
  const clear = () => {
    if (id != null) clearTimeout(id);
    id = null;
  };
  const promise = new Promise((resolve) => {
    id = setTimeout(() => resolve('timeout'), ms);
  });
  return { promise, clear };
}

export async function awaitWithBudget(work, {
  budgetMs = 1500,
  createDelay = createPushCleanupDelay,
} = {}) {
  const delay = createDelay(budgetMs);
  const settled = Promise.resolve()
    .then(() => work())
    .then(
      (value) => ({ kind: 'settled', value }),
      (error) => ({ kind: 'rejected', error }),
    );
  try {
    const winner = await Promise.race([
      settled,
      Promise.resolve(delay?.promise).then(() => ({ kind: 'timeout' })),
    ]);
    if (winner.kind !== 'timeout') delay?.clear?.();
    return winner;
  } catch {
    delay?.clear?.();
    return { kind: 'rejected' };
  }
}

/** Test helper — reset default module state between suites. */
export function __resetProspectPushSessionForTests(epoch = 1, generation = 1) {
  defaultSession.__resetForTests(epoch, generation);
}
